// ============================================================
// یکسان‌سازی متن فارسی — پایه‌ی هر سه لایه‌ی دستیار
//
// بدون این تابع «تکنولوژي» با «تکنولوژی» دو کلمه‌ی متفاوت حساب
// می‌شوند و تطبیق شکست می‌خورد. تطبیق در تمام دستیار بر پایه‌ی
// includes است، نه ریشه‌یابی؛ پس هر صورت محاوره‌ای باید جداگانه
// در فهرست الگوها بیاید.
// ============================================================

const PERSIAN_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const ARABIC_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

/** ارقام فارسی و عربی را به ارقام انگلیسی تبدیل می‌کند. */
export const toEnglishDigits = (input: unknown): string => {
    let result = String(input ?? "");
    for (let i = 0; i < 10; i++) {
        result = result.split(PERSIAN_DIGITS[i]).join(String(i));
        result = result.split(ARABIC_DIGITS[i]).join(String(i));
    }
    return result;
};

/** ارقام انگلیسی را برای نمایش به فارسی تبدیل می‌کند. */
export const toPersianDigits = (input: unknown): string => {
    const text = String(input ?? "");
    let result = "";
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        result += code >= 48 && code <= 57 ? PERSIAN_DIGITS[code - 48] : text[i];
    }
    return result;
};

/**
 * متن را برای تطبیق یکسان می‌کند.
 *
 * نکته‌ی ظریف: نیم‌فاصله به «فاصله» تبدیل می‌شود نه به هیچ. پس
 * «پروژه‌های» می‌شود «پروژه های» — یعنی الگوها باید با فاصله
 * نوشته شوند، نه سرهم.
 */
export const normalize = (text: unknown): string =>
    toEnglishDigits(text)
        .toLowerCase()
        // نیم‌فاصله، فاصله‌ی مجازی و نویسه‌های جهت‌دهی
        .replace(/[​-‏‪-‮⁠﻿]/g, " ")
        .replace(/ي/g, "ی")
        .replace(/ك/g, "ک")
        .replace(/[أإآٱ]/g, "ا")
        .replace(/[ۀة]/g, "ه")
        .replace(/ؤ/g, "و")
        // اعراب
        .replace(/[ً-ْٰ]/g, "")
        // نقطه‌گذاری → فاصله (فارسی، لاتین و رقم نگه داشته می‌شوند)
        .replace(/[^ؠ-ۿa-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

/** آیا هر یک از الگوها در متن یکسان‌شده هست؟ */
export const hasAny = (normalized: string, patterns: string[]): boolean =>
    patterns.some((p) => normalized.indexOf(normalize(p)) !== -1);

/** بلندترین الگویی که در متن پیدا شد (برای امتیازدهی بر اساس طول تگ). */
export const longestMatch = (normalized: string, patterns: string[]): string | null => {
    let best = "";
    for (let i = 0; i < patterns.length; i++) {
        const candidate = normalize(patterns[i]);
        if (candidate && normalized.indexOf(candidate) !== -1 && candidate.length > best.length) {
            best = candidate;
        }
    }
    return best || null;
};

/**
 * الگوها را از جمله حذف می‌کند تا «باقی‌مانده»ی جمله بماند.
 * برای بیرون کشیدن نام از «اطلاعات خبازی رضا محمدی رو بده» لازم است.
 *
 * دو مرحله دارد و این تفکیک حیاتی است:
 *
 *   ۱) عبارت‌های چندکلمه‌ای («وظایف راننده») به صورت زیررشته حذف
 *      می‌شوند؛ بلندترها اول، تا «گزارش دهه» قبل از «گزارش» برود.
 *   ۲) واژه‌های تک («رو»، «در»، «بده») فقط اگر *کل* یک کلمه باشند
 *      حذف می‌شوند. حذف زیررشته‌ای این‌ها اسم‌ها را نابود می‌کرد:
 *      «بهروز» می‌شد «بهز» و «نادر» می‌شد «نا».
 */
export const stripPatterns = (text: string, patterns: string[]): string => {
    let working = normalize(text);

    const cleaned = patterns.map((p) => normalize(p)).filter(Boolean);

    cleaned
        .filter((p) => p.indexOf(" ") !== -1)
        .sort((a, b) => b.length - a.length)
        .forEach((phrase) => {
            working = working.split(phrase).join(" ");
        });

    const singles = cleaned.filter((p) => p.indexOf(" ") === -1);

    return working
        .split(" ")
        .filter((word) => word && singles.indexOf(word) === -1)
        .join(" ")
        .trim();
};
